/* eslint-disable camelcase */
import qs from 'qs'
import { isNil } from 'ramda'

import type {
  ArticleListResponse,
  ArticleResponse,
  CategoryListResponse,
  CategoryListResponseDataItemCustom,
  CategoryResponse,
  GlobalResponse,
  NavigationResponse,
  PageListResponse,
  PageResponse,
  SearchArticlesResponse,
  SectionsEshopsComponent,
} from '@twisto/api-cms'
import type { BenefitsData, EshopsData } from '@twisto/api-cms/custom-types'

import type { SectionType } from '../components'

export type EshopCategory = NonNullable<SectionsEshopsComponent['category']>

export const CATEGORY_TO_INCLUDE_PARAM: Record<
  EshopCategory,
  keyof EshopsData
> = {
  popular: 'popular_eshops',
  newest: 'newest_eshops',
  'pay-in-3': 'pay_in_three_eshops',
  'pay-in-30': 'merchant_benefits',
}

export const ADDITIONAL_DATA_ENPOINTS = {
  benefits: 'benefits',
  eshops: {
    popular: 'eshops/popular',
    list: 'eshops/list',
  },
}

type StandardPageResponse = PageResponse & {
  data?: {
    title: string
    subtitle: string
    isRoot?: boolean
  }
  attributes: { contentSections?: SectionType[] }
}

type RootPageResponse = PageResponse & {
  title: string
  subtitle: string
  isRoot?: boolean
  attributes: { contentSections?: SectionType[] }
}

export const getStrapiURL = (path: string) =>
  `${process.env.STRAPI_API_URL ?? 'http://localhost:1337/api'}${path}`

export const getQueryString = (params: Record<string, unknown>): string => {
  const query = qs.stringify(params, {
    encodeValuesOnly: true,
  })

  return query.length > 0 ? `?${query}` : ''
}

const fetchAPI = async (
  requestUrl: string,
  urlParamsObject = {},
  options = {}
) => {
  const mergedOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  }

  const queryString = getQueryString(urlParamsObject)
  try {
    const response = await fetch(`${requestUrl}${queryString}`, mergedOptions)
    const data = await response.json()

    return data
  } catch (error) {
    return {}
  }
}

const fetchStrapiAPI = async (
  path: string,
  urlParamsObject = {},
  options = {}
) => {
  const requestUrl = getStrapiURL(path)
  // eslint-disable-next-line no-console
  console.log(requestUrl)

  return await fetchAPI(requestUrl, urlParamsObject, options)
}

export const getPages = async (): Promise<PageListResponse> =>
  await fetchStrapiAPI('/pages', { fields: 'path' })

export const getPage = async (url: string): Promise<StandardPageResponse> =>
  await fetchStrapiAPI(`/page/${encodeURIComponent(url)}`)

export const getConditionsPage = async (
  url: string
): Promise<StandardPageResponse> =>
  await fetchStrapiAPI(`/conditions-page/${encodeURIComponent(url)}`)

export const getConditionsRootPage = async (): Promise<RootPageResponse> =>
  await fetchStrapiAPI(`/conditions-page/root`)

// TODO: Refactor return types
export const getConditionsSection = async (): Promise<PageResponse> =>
  await fetchStrapiAPI(`/conditions-sections`, {
    populate: {
      conditions_pages: true,
      conditions_folders: {
        populate: ['conditions_pages'],
      },
    },
  })

export const getNavigation = async (id: number): Promise<NavigationResponse> =>
  await fetchStrapiAPI(`/navigations/${id}`)

export const getGlobalMetadata = async (): Promise<GlobalResponse> =>
  await fetchStrapiAPI('/global')

export const getCategories = async (): Promise<CategoryListResponse> =>
  await fetchStrapiAPI('/categories', { fields: 'path' })

export const getCategory = async (
  url: string
): Promise<
  CategoryResponse & {
    data: CategoryListResponseDataItemCustom
  }
> => await fetchStrapiAPI(`/categories/${encodeURIComponent(url)}`)

export const getArticle = async (url: string): Promise<ArticleResponse> =>
  await fetchStrapiAPI(`/articles/${encodeURIComponent(url)}`)

export const getArticles = async (): Promise<ArticleListResponse> =>
  await fetchStrapiAPI('/articles', { fields: 'path' })

export const getPopularArticles = async (
  rootCategoryId?: number
): Promise<ArticleListResponse> =>
  await fetchStrapiAPI('/articles', {
    'filters[isPopular]': true,
    'pagination[pageSize]': 15,
    ...(!isNil(rootCategoryId) && {
      'filters[rootCategoryId]': rootCategoryId,
    }),
  })

export const searchArticles = async (
  searchTerm: string,
  pageSize: number,
  rootCategoryId?: number
): Promise<SearchArticlesResponse> =>
  await fetchAPI('/api/article-search', {
    query: encodeURIComponent(searchTerm),
    'pagination[articles][pageSize]': pageSize,
    ...(!isNil(rootCategoryId) && {
      'filters[articles][rootCategoryId]': rootCategoryId,
    }),
  })

export const getAdditionalData = async <T>(
  endpoint?: string
): Promise<T | null> => {
  const rawStrapiUrl = process.env.CMS_API_URL ?? ''
  const apiUrl = rawStrapiUrl
    .replace(/https?:\/\/cms\./, 'https://api.')
    .replace(/\/api$/, '')

  const url = `${apiUrl}/${endpoint ?? ''}`

  if (!url) {
    return null
  }

  return await fetchAPI(url)
}

export const getEshops = async (
  endpoint: keyof typeof ADDITIONAL_DATA_ENPOINTS.eshops,
  category?: string
): Promise<EshopsData | null> => {
  const requestUrl = `${ADDITIONAL_DATA_ENPOINTS.eshops[endpoint]}${category ? `?include=${category}` : ''}`

  if (!requestUrl) {
    return null
  }

  return await getAdditionalData<EshopsData>(requestUrl).then((data) => {
    if (!data) {
      return null
    }

    return data
  })
}

export const getBenefits = async (): Promise<BenefitsData | null> => {
  const requestUrl = ADDITIONAL_DATA_ENPOINTS.benefits

  return await getAdditionalData<BenefitsData>(requestUrl).then((data) => {
    if (!data) {
      return null
    }

    return data
  })
}

export const validateInvoiceId = async (
  invoiceId: string,
  email: string
): Promise<{ valid: boolean } | null> => {
  const apiOrigin = window.location.origin.replace('//www.', '//api.')

  const response = await fetch(`${apiOrigin}/invoice/validate/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      invoice_id: invoiceId,
      email,
    }),
  })

  if (!response.ok) {
    return null
  }

  const data = await response.json()

  return data
}
